import Failure from '../../../core/Failure';
import HttpClient from '../../../core/HttpClient';
import { Authentication } from '@leedpulse-monorepo/classes';

export interface SenderMigrationBlocker {
  code: string;
  message: string;
  details: string[];
}

/** Non-blocking: worth reading before confirming, not a reason to stop. */
export interface SenderMigrationWarning {
  code: string;
  message: string;
  details: string[];
}

export interface SenderMigrationCount {
  collection: string;
  /** Documents that will be migrated (this subscription's, plus the orphans). */
  total: number;
  /** Among them, those carrying no api_key at all. */
  orphans: number;
  /** Documents of a previous tenant of the number, deliberately left behind. */
  foreign: number;
}

/** One field the apply phase will write, shown before confirmation. */
export interface SenderMigrationChange {
  target: string;
  from: string;
  to: string;
}

/** Another number carrying contacts of this subscription. */
export interface SenderMigrationOtherSender {
  sender: string;
  contacts: number;
  /** Still in the subscription's sender[] — a live second line, not a stray. */
  still_held: boolean;
  selected: boolean;
}

export interface SenderMigrationPlan {
  subscription_id: string;
  subscription_name: string;
  api_key: string;
  old_sender: string;
  new_sender: string;
  /** No number is replaced: documents are only pulled onto new_sender. */
  repatriate_only: boolean;
  /** Leedpulse only: contacts, SMS, calls and voicemails carry the number and are rewritten. */
  rewrites_history: boolean;
  redirection: string;
  redirection_source: 'param' | 'old_number' | 'new_number' | 'none';
  blockers: SenderMigrationBlocker[];
  warnings: SenderMigrationWarning[];
  counts: SenderMigrationCount[];
  other_senders: SenderMigrationOtherSender[];
  total_documents: number;
  /** Too big to answer within one request: the apply runs detached. */
  exceeds_sync_limit: boolean;
  changes: SenderMigrationChange[];
}

export interface SenderMigrationResult {
  migrated_documents: number;
  remaining_on_old: SenderMigrationCount[];
  log_id: string;
  elevenlabs_import: 'already' | 'imported' | 'failed' | 'skipped';
}

export interface SenderMigrationResponse {
  plan: SenderMigrationPlan;
  applied: boolean;
  /** The apply was started and left running: no result to show, come back and re-analyse. */
  detached: boolean;
  result: SenderMigrationResult | null;
}

interface MigrateSubscriptionSenderPayload {
  subscription_id: string;
  /** Omit to only pull other numbers' documents onto new_sender. */
  old_sender?: string;
  new_sender: string;
  redirection?: string;
  additional_senders?: string[];
  apply: boolean;
}

async function migrateSubscriptionSender(
  payload: MigrateSubscriptionSenderPayload,
  auth: Authentication
): Promise<SenderMigrationResponse> {
  try {
    const response = await HttpClient.post('/subscription/sender/migrate', payload, auth.token);
    return response.data;
  } catch (e) {
    console.error('migrateSubscriptionSender error:', e);
    throw new MigrateSubscriptionSenderFailure(e instanceof Error ? e.message : undefined);
  }
}

export class MigrateSubscriptionSenderFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default migrateSubscriptionSender;
